import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  EditOutlined,
  WarningOutlined,
  InfoCircleFilled,
  LikeFilled,
  DislikeFilled,
  MoreOutlined
} from '@ant-design/icons';
import { Button, Col, Row, Switch, Table, Tooltip } from 'antd';

import CustomCopyToClipboard from 'components/CustomCopyToClipboard';
import Truncate from 'components/Truncate';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { useListProductCampaigns, useEditProductCampaign } from 'v2/hooks/campaigns';
import type { CampaignLinkType } from 'v2/services/campaigns';
import { useUserStore } from 'v2/store/User';

import type { ICheckoutsUrls } from '../..';

const TAB_TO_LINK_TYPE: Record<string, CampaignLinkType> = {
  'producer': 'producerLinks',
  'affiliate': 'affiliatesLinks',
  'my-links': 'myLinks'
};

interface IProps {
  type: 'producer' | 'affiliate' | 'eduzz' | 'my-links';
  openUrlCreator: (campaign: string) => void;
  openUrlCreatorCheckout: (urls: ICheckoutsUrls) => void;
  openQuickView?: any;
  isProducer?: boolean;
  setUpdate: any;
  productId: number;
}

const CampaignTable: React.FC<IProps> = ({ type, openUrlCreator, openQuickView, isProducer, setUpdate, productId }) => {
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 15 });

  const linkType = TAB_TO_LINK_TYPE[type] ?? 'producerLinks';

  const {
    data: campaignsResponse,
    isLoading: isLoadingCampaigns,
    refetch
  } = useListProductCampaigns(productId, linkType, pagination);
  const editMutation = useEditProductCampaign();

  const campaigns = campaignsResponse?.data ?? [];
  const totalItems = campaignsResponse?.pagination?.total_items ?? 0;

  const { currentUserId } = useUserStore(({ user }) => ({ currentUserId: user.id }));

  useEffect(() => {
    setUpdate(() => refetch());
  }, [setUpdate, refetch]);

  const handleChangeStatusCampaign = useCallback(
    async (idCampaign: number, status: boolean) => {
      try {
        await editMutation.mutateAsync({ productId, campaignId: idCampaign, data: { active: status } });
        toast.success('Status atualizado com sucesso');
      } catch (error) {
        toast.error('Ops, erro na alteração de status');
      }
    },
    [productId, editMutation]
  );

  const handleClickStatusCampaignAffiliate = useCallback(
    async (idCampaign: number, status: string) => {
      try {
        await editMutation.mutateAsync({ productId, campaignId: idCampaign, data: { status } });
        toast.success('Status atualizado com sucesso');
      } catch (error) {
        toast.error('Ops, erro na alteração de status do afiliado');
      }
    },
    [productId, editMutation]
  );

  const onChangePage = useCallback((_event: any, page: number) => {
    setPagination(prev => ({ ...prev, current_page: page + 1 }));
  }, []);

  const onChangeRowsPerPage = useCallback((event: any) => {
    setPagination(prev => ({ ...prev, per_page: event.target.value, current_page: 1 }));
  }, []);

  const columns = [
    {
      title: 'Status',
      key: 'status',
      width: 80,
      className: 'text-[#192542] text-sm py-1 px-6 max-w-[450px] [&_.ant-table-row-expand-icon]:hidden!',
      render: (_: any, item: any) => (
        <div className='flex items-center'>
          <Tooltip title={item.active ? 'Ativo' : 'Inativo'} placement='top'>
            <div>
              <Switch
                checked={item.active}
                onClick={() => handleChangeStatusCampaign(item.id, item.active ? false : true)}
                disabled={type === 'affiliate' || (type === 'producer' && !isProducer)}
                size='small'
              />
            </div>
          </Tooltip>
          {type === 'my-links' && item.status === 'R' && (
            <Tooltip
              placement='top'
              title='Essa campanha foi reprovada pelo produtor e o redirecionamento será feito para a página de venda padrão da Eduzz'
            >
              <WarningOutlined style={{ fontSize: 16, marginLeft: '0.5rem', color: 'red' }} />
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: 'Chave',
      dataIndex: 'key',
      key: 'key',
      width: 80,
      className: 'text-[#192542] text-sm py-1 px-6 max-w-[450px]'
    },
    {
      title: 'FI',
      key: 'fi',
      width: 60,
      className: 'text-[#192542] text-sm py-1 px-6 max-w-[450px]',
      render: (_: any, item: any) =>
        item.infinity_funnel ? (
          <Tooltip placement='top' title='Verificado'>
            <div style={{ display: 'block' }}>
              <img style={{ width: 24 }} src='/img/icon_funil_ativo.png' alt='Funil' />
            </div>
          </Tooltip>
        ) : (
          <Tooltip placement='top' title='Não verificado'>
            <div style={{ display: 'block' }}>
              <img style={{ width: 24 }} src='/img/icon_funil.png' alt='Funil' />
            </div>
          </Tooltip>
        )
    },
    {
      title: 'Campanha',
      key: 'campaign',
      width: 200,
      className: 'text-[#192542] text-sm py-1 px-6 max-w-[450px]',
      render: (_: any, item: any) => (
        <Truncate count='25' final='...'>
          {item.title}
        </Truncate>
      )
    },
    {
      title: 'URL divulgação',
      key: 'url',
      className: 'text-[#192542] text-sm py-1 px-6 max-w-[450px]',
      render: (_: any, item: any) => (
        <Row gutter={16} justify='start' align='middle'>
          <Col span={12}>
            <CustomCopyToClipboard disableUnderline text={`https://edzz.la/${item.key}?a=${currentUserId}`} />
          </Col>
          <Col span={12}>
            <PermissionHide action='myeduzz:links:editar'>
              <Button
                onClick={() => openUrlCreator(`https://edzz.la/${item.key}?a=${currentUserId}`)}
                icon={<EditOutlined />}
              >
                Criador de URL
              </Button>
            </PermissionHide>
          </Col>
          <Col span={24}>
            <div className='text-grey-400 hover:text-text-heading mt-2'>
              <Truncate
                count='512'
                final='...'
                after={
                  <Fragment>
                    {!item.valid_url && item.url.length > 0 && (
                      <Tooltip
                        placement='bottom'
                        title='Essa URL não possui script de rastreio validado pela Eduzz. Utilize a URL acima para garantir o ganho de comissões.'
                      >
                        <span className='text-grey-400 hover:text-text-heading ml-[0.5rem] text-[12px]'>
                          <InfoCircleFilled />
                        </span>
                      </Tooltip>
                    )}
                  </Fragment>
                }
              >
                {`${item.url}?a=${currentUserId}`}
              </Truncate>
            </div>
          </Col>
        </Row>
      )
    },
    {
      title: 'RPC/A',
      dataIndex: 'rpc_affiliate',
      key: 'rpc_affiliate',
      width: 80,
      align: 'right' as const,
      className: 'text-[#192542] text-sm py-1 px-6 max-w-[450px]'
    },
    {
      title: 'RPC/P',
      dataIndex: 'rpc_producer',
      key: 'rpc_producer',
      width: 80,
      align: 'right' as const,
      className: 'text-[#192542] text-sm py-1 px-6 max-w-[450px]'
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      fixed: 'right' as const,
      width: type === 'affiliate' ? 120 : 60,
      className: 'text-[#192542] text-sm py-1 px-6 max-w-[450px]',
      render: (_: any, item: any) => (
        <div className='flex items-center justify-end gap-1'>
          {(type === 'producer' || type === 'my-links') && (
            <Button type='text' shape='circle' icon={<MoreOutlined />} onClick={() => openQuickView(item)} />
          )}
          {item.can_moderate && type === 'affiliate' && (
            <div className='flex'>
              <Button
                type='text'
                shape='circle'
                className={item.status === 'A' ? 'text-[#009358]!' : 'hover:text-[#009358]!'}
                disabled={item.status === 'A'}
                onClick={() => handleClickStatusCampaignAffiliate(item.id, 'A')}
                icon={<LikeFilled />}
              />
              <Button
                type='text'
                shape='circle'
                className={item.status === 'R' ? 'text-[#DC3F53]!' : 'hover:text-[#DC3F53]!'}
                disabled={item.status === 'R'}
                onClick={() => handleClickStatusCampaignAffiliate(item.id, 'R')}
                icon={<DislikeFilled />}
              />
            </div>
          )}
        </div>
      )
    }
  ];
  return (
    <div>
      <div>
        {type !== 'eduzz' && (
          <Table
            scroll={{ x: 1000 }}
            columns={columns}
            dataSource={campaigns}
            rowKey={(item: any) => `cpg${item.id}`}
            loading={isLoadingCampaigns}
            pagination={{
              current: pagination.current_page,
              pageSize: pagination.per_page,
              total: totalItems,
              showSizeChanger: true,
              onChange: onChangePage,
              onShowSizeChange: onChangeRowsPerPage
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CampaignTable;
